/**
 * Orquestador de las Guías in-app: QUÉ guía se dispara sola y CUÁNDO. Lógica pura, sin React ni
 * DOM, para que cada regla del plan (§7) tenga su test con su control negativo.
 *
 * Qué guías le tocan al usuario lo decide el Backend (`guides/eligible/`): acá sólo se aplica el
 * RITMO — una por vez, una por sesión, el cooldown entre automáticas y los momentos en que no se
 * interrumpe (modal abierto, túnel de alta, teléfono, recién cargada la app).
 */
import {anchorAvailableInUi} from './anchors';
import {
    APP_WARMUP_MS,
    MAX_AUTO_PER_SESSION,
    MIN_AUTO_VIEWPORT_PX,
    MODE_AUTO,
} from './constants';
import {frameOf, sameGuideScreen} from './screens';

export const BLOCK_ACTIVE_GUIDE = 'active_guide';
export const BLOCK_ACTIVE_ELSEWHERE = 'active_elsewhere';
export const BLOCK_SESSION_CAP = 'session_cap';
export const BLOCK_COOLDOWN = 'cooldown';
export const BLOCK_SCREEN_USED = 'screen_used';
export const BLOCK_WARMUP = 'warmup';
export const BLOCK_MODAL_OPEN = 'modal_open';
export const BLOCK_SMALL_VIEWPORT = 'small_viewport';
export const BLOCK_ONBOARDING = 'onboarding';

/**
 * Lo que frena a una guía común pero NO a un recorrido de primera visita (contrato §3 de la tanda
 * 2026-10-02-recorrido-ux21): el tope de una automática por sesión, la espera entre automáticas y
 * una guía automática que quedó esperando en otra pantalla. Al recorrido sí lo frena todo lo demás,
 * incluida una automática por PANTALLA por sesión, para no encimar.
 */
const REGULAR_ONLY_BLOCKERS = [BLOCK_SESSION_CAP, BLOCK_COOLDOWN, BLOCK_ACTIVE_ELSEWHERE];

export function isFirstVisitGuide(guide) {
    return !!guide && guide.first_visit === true;
}

/**
 * Los bloqueos que valen para ESTA guía. Sólo un recorrido que NUNCA se vio (sin `user_state` de esta
 * versión) se saltea el ritmo de las comunes: retomar uno que quedó a medias sigue el tope y la espera
 * de 48 h, o volvía a salir en cada pestaña nueva el mismo día (review C-rec-guias-01).
 */
export function blockersFor(guide, blockers) {
    if (!isFirstVisitGuide(guide) || guide.user_state) return blockers;
    return blockers.filter((blocker) => !REGULAR_ONLY_BLOCKERS.includes(blocker));
}

/**
 * Un paso vive en la pantalla que dice; uno sin pantalla (un modal de anuncio) va en cualquiera.
 * Dos rutas que dibujan la misma pantalla cuentan como una (`sameGuideScreen`). `frame` es el
 * `uiVersion` o `{uiVersion, dashboardIsTablero, alertsCenter}` (ver `screens.js`).
 */
export function stepMatchesScreen(step, screen, frame) {
    if (!step) return false;
    return !step.screen || sameGuideScreen(step.screen, screen, frame);
}

/** Un modal va en cualquier marco; un paso anclado, sólo donde su elemento existe. */
export function stepAvailableInUi(step, frame) {
    if (!step) return false;
    return step.type === 'modal' || anchorAvailableInUi(step.anchor, frameOf(frame).uiVersion);
}

/** El paso se puede dibujar ACÁ: su pantalla y su marco. */
export function stepRunnableOn(step, screen, frame) {
    return stepMatchesScreen(step, screen, frame) && stepAvailableInUi(step, frame);
}

/**
 * Una guía automática que espera en OTRA pantalla: su paso actual vive en una pantalla que el
 * usuario dejó. En silencio espera a que vuelva; mientras tanto no tapa nada acá.
 */
export function isWaitingElsewhere(active, screen, frame) {
    if (!active || active.mode !== MODE_AUTO) return false;
    const step = active.guide && active.guide.steps ? active.guide.steps[active.index] : null;
    return !!step && !stepMatchesScreen(step, screen, frame);
}

/** Una guía sin pantalla de disparo es el anuncio general: arranca en cualquier pantalla con marco. */
export function guideTriggersOn(guide, screen, frame) {
    const triggerScreen = guide && guide.trigger ? guide.trigger.screen : null;
    return !triggerScreen || sameGuideScreen(triggerScreen, screen, frame);
}

/**
 * Un paso OPCIONAL apunta a algo que existe sólo si hay datos (la primera fila de una lista, una
 * línea del Tablero): si su elemento no está, se saltea en silencio, sin «ancla rota» y sin aplazar
 * el recorrido. Un modal nunca es opcional (no tiene elemento que pueda faltar).
 */
export function isOptionalStep(step) {
    return !!step && step.type !== 'modal' && step.optional === true;
}

/**
 * ¿La pantalla ya cargó, para lo que a esta guía le importa? Sí si alguna ancla FIJA de la guía
 * (un paso anclado no opcional) que vive en esta pantalla está dibujada. Sin eso, que falte el
 * elemento de un opcional no dice nada: el Tablero, mientras trae sus líneas, es sólo un loader —sin
 * tabla, filtros ni «Columnas»— y dar la fila por ausente salteaba justo lo que había que mostrar
 * (review C-rec-guias-01). Una guía sin anclas fijas en esta pantalla nunca se da por cargada.
 */
export function screenSettled(steps, {screen, frame, isAnchorDrawable}) {
    return (steps || []).some((step) => step && step.type !== 'modal' && !isOptionalStep(step)
        && stepRunnableOn(step, screen, frame) && isAnchorDrawable(step.anchor));
}

/**
 * El primer paso desde `from` (inclusive) que vale la pena dibujar: saltea los opcionales de ESTA
 * pantalla cuyo elemento no está (`isAnchorDrawable`), pero sólo con la pantalla cargada
 * (`screenSettled`): así tres opcionales seguidos que no aplican no son tres esperas mudas, y una
 * pantalla que todavía carga no se lee como «no hay datos». Un opcional de otra pantalla no se juzga
 * desde acá. Devuelve `steps.length` si no queda ninguno.
 */
export function nextDrawableIndex(steps, from, {screen, frame, isAnchorDrawable}) {
    if (!screenSettled(steps, {screen, frame, isAnchorDrawable})) return from;
    let index = from;
    while (index < steps.length && isOptionalStep(steps[index])
        && stepRunnableOn(steps[index], screen, frame) && !isAnchorDrawable(steps[index].anchor)) {
        index += 1;
    }
    return index;
}

/** El paso anterior que NO se salteó, o null: «Anterior» no puede volver a un paso que no está. */
export function previousShownIndex(index, skipped = []) {
    for (let candidate = index - 1; candidate >= 0; candidate -= 1) {
        if (!skipped.includes(candidate)) return candidate;
    }
    return null;
}

/**
 * El «x de y» de la tarjeta sin contar lo que no se va a ver: los pasos ya salteados y los opcionales
 * de adelante que `isProjectedAbsent` da por ausentes. Sin esto, el Tablero de una agencia sin líneas
 * decía «1 de 7» y saltaba a «5 de 7». `isLast` = no queda nada para dibujar después de éste.
 */
export function guideProgress(steps, index, skipped = [], isProjectedAbsent = () => false) {
    const shownBefore = steps.slice(0, index).filter((_, i) => !skipped.includes(i)).length;
    const ahead = steps.slice(index + 1)
        .filter((step, offset) => !skipped.includes(index + 1 + offset) && !isProjectedAbsent(step)).length;
    return {position: shownBefore, total: shownBefore + 1 + ahead, isLast: ahead === 0};
}

/** Dónde retomar: el paso guardado si la guía quedó a medias, si no el primero. */
export function resumeIndex(guide) {
    const steps = (guide && guide.steps) || [];
    const saved = guide && guide.user_state ? Number(guide.user_state.current_step) || 0 : 0;
    return Math.min(Math.max(saved, 0), Math.max(steps.length - 1, 0));
}

/**
 * El paso con el que ARRANCA una guía automática en esta pantalla (y en este marco), o null si no
 * puede arrancar acá: una guía de un hotspot de la barra 2.1 no se dispara en el clásico.
 * Si el paso guardado vive en otra pantalla, se reintenta desde el primero: retomar a mitad de un
 * tour que cruza pantallas sin el contexto del paso anterior confunde más de lo que ayuda.
 */
export function startIndexOn(guide, screen, frame) {
    const steps = (guide && guide.steps) || [];
    const resumed = resumeIndex(guide);
    if (stepRunnableOn(steps[resumed], screen, frame)) return resumed;
    if (stepRunnableOn(steps[0], screen, frame)) return 0;
    return null;
}

/**
 * Los motivos por los que AHORA no se puede disparar nada solo. Vacío = se puede.
 * Recibe todo por parámetro (nada de `window` acá) para que los tests fijen cada condición.
 */
export function autoBlockers({
    hasActiveGuide = false,
    activeWaitingElsewhere = false, // la guía activa es automática y espera en otra pantalla
    autoShownThisSession = 0,
    cooldownUntil = null,
    screenUsedThisSession = false, // ya se mostró una automática en ESTA pantalla en la sesión
    now = Date.now(),
    appStartedAt = 0,
    modalOpen = false, // cualquier capa abierta: modal de Bootstrap, panel lateral, cajón
    viewportWidth = Infinity,
    pathname = '',
}) {
    const blockers = [];
    if (hasActiveGuide) blockers.push(activeWaitingElsewhere ? BLOCK_ACTIVE_ELSEWHERE : BLOCK_ACTIVE_GUIDE);
    if (autoShownThisSession >= MAX_AUTO_PER_SESSION) blockers.push(BLOCK_SESSION_CAP);
    if (cooldownUntil && new Date(cooldownUntil).getTime() > now) blockers.push(BLOCK_COOLDOWN);
    if (screenUsedThisSession) blockers.push(BLOCK_SCREEN_USED);
    if (now - appStartedAt < APP_WARMUP_MS) blockers.push(BLOCK_WARMUP);
    if (modalOpen) blockers.push(BLOCK_MODAL_OPEN);
    if (viewportWidth < MIN_AUTO_VIEWPORT_PX) blockers.push(BLOCK_SMALL_VIEWPORT);
    if (pathname.startsWith('/onboarding')) blockers.push(BLOCK_ONBOARDING);
    return blockers;
}

/**
 * La guía que se dispara sola en esta pantalla, o null. `eligible` ya viene ordenada por prioridad
 * desde el Backend; gana la primera que puede arrancar acá. Las demás esperan a otra sesión.
 * Los bloqueos se miran POR GUÍA (`blockersFor`): con el cupo de la sesión gastado, un recorrido de
 * primera visita de esta pantalla todavía puede salir.
 *
 * @returns {{guide: object|null, index: number|null, blockers: string[]}}
 */
export function pickAutoGuide({eligible = [], screen, uiVersion, frame = uiVersion, blockers = []}) {
    if (!screen) return {guide: null, index: null, blockers};
    for (const guide of eligible) {
        if (blockersFor(guide, blockers).length > 0) continue;
        if (!guideTriggersOn(guide, screen, frame)) continue;
        const index = startIndexOn(guide, screen, frame);
        if (index !== null) return {guide, index, blockers};
    }
    return {guide: null, index: null, blockers};
}
